/**
 * 玩家回放 - ASR 字幕流
 *
 * 按时间升序展示玩家/NPC 对话，按 speaker_role 区分颜色。
 * 支持选中某条 utterance 后回传给 TimelineView。
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Card,
  List,
  Tag,
  Empty,
  Skeleton,
  Pagination,
  Space,
  message,
} from 'antd'
import dayjs from 'dayjs'

import { playbackApi } from '@/services/api'
import type { PlaybackUtteranceItem } from '@/types/playback'

interface Props {
  worldId: string
  playerId: string
  onSelect?: (utteranceSeq: number | null) => void
  selectedSeq?: number | null
}

const PAGE_SIZE = 50

const SPEAKER_COLOR: Record<string, string> = {
  player: '#e6f4ff',
  actor: '#f6ffed',
  unknown: '#fafafa',
}

const SPEAKER_TAG_COLOR: Record<string, string> = {
  player: 'blue',
  actor: 'green',
  unknown: 'default',
}

const AsrSubtitles: React.FC<Props> = ({
  worldId,
  playerId,
  onSelect,
  selectedSeq,
}) => {
  const [loading, setLoading] = useState(false)
  const [items, setItems] = useState<PlaybackUtteranceItem[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)

  const fetchUtterances = useCallback(async () => {
    setLoading(true)
    try {
      const response = await playbackApi.getUtterances(worldId, playerId, {
        page,
        page_size: PAGE_SIZE,
      })
      setItems(response.data?.items ?? [])
      setTotal(response.data?.total ?? 0)
    } catch (error: any) {
      message.error(error?.message ?? '加载 ASR 字幕失败')
      console.error('fetchUtterances error:', error)
    } finally {
      setLoading(false)
    }
  }, [worldId, playerId, page])

  useEffect(() => {
    fetchUtterances()
  }, [fetchUtterances])

  // Auto-scroll to selected
  useEffect(() => {
    if (selectedSeq == null) return
    const el = document.getElementById(`utterance-${selectedSeq}`)
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }, [selectedSeq])

  const headerMeta = useMemo(
    () => (
      <Space>
        <span>ASR 字幕流</span>
        <Tag>{total} 条</Tag>
      </Space>
    ),
    [total],
  )

  return (
    <Card title={headerMeta} size="small">
      {loading && items.length === 0 ? (
        <Skeleton active />
      ) : items.length === 0 ? (
        <Empty description="暂无 ASR 字幕" />
      ) : (
        <>
          <List
            dataSource={items}
            renderItem={(item) => {
              const isSelected = item.utterance_seq === selectedSeq
              return (
                <List.Item
                  id={`utterance-${item.utterance_seq ?? 'none'}`}
                  style={{
                    background: isSelected ? '#fffbe6' : SPEAKER_COLOR[item.speaker_role] ?? '#fafafa',
                    cursor: onSelect ? 'pointer' : 'default',
                    padding: '8px 12px',
                    borderRadius: 4,
                    marginBottom: 4,
                    border: isSelected ? '1px solid #faad14' : '1px solid transparent',
                  }}
                  onClick={() => onSelect?.(item.utterance_seq ?? null)}
                >
                  <List.Item.Meta
                    title={
                      <Space>
                        <Tag color={SPEAKER_TAG_COLOR[item.speaker_role] ?? 'default'}>
                          {item.speaker_role}
                        </Tag>
                        {item.speaker_name && <strong>{item.speaker_name}</strong>}
                        <span style={{ fontSize: 12, color: '#999' }}>
                          {dayjs(item.occurred_at).format('HH:mm:ss')}
                        </span>
                        {item.utterance_seq != null && (
                          <span style={{ fontSize: 11, color: '#bbb' }}>
                            #{item.utterance_seq}
                          </span>
                        )}
                      </Space>
                    }
                    description={
                      <Space direction="vertical" size={0} style={{ width: '100%' }}>
                        <span>{item.text}</span>
                        {(item.act_id || item.node_id) && (
                          <span style={{ fontSize: 11, color: '#aaa' }}>
                            {item.act_id ? `act=${item.act_id}` : ''}
                            {item.act_id && item.node_id ? ' ' : ''}
                            {item.node_id ? `node=${item.node_id}` : ''}
                          </span>
                        )}
                      </Space>
                    }
                  />
                </List.Item>
              )
            }}
          />
          <Pagination
            className="mt-4 text-right"
            current={page}
            pageSize={PAGE_SIZE}
            total={total}
            onChange={setPage}
            showSizeChanger={false}
          />
        </>
      )}
    </Card>
  )
}

export default AsrSubtitles